import React, { useEffect, useMemo, useState, useCallback } from "react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../ui/dialog";
import { toast } from "sonner";
import {
  ShieldCheck, ShieldAlert, Lock, Mail, CreditCard, Save, Trash2,
  AlertTriangle, CheckCircle2, XCircle, Eye, EyeOff, Send, KeyRound, Loader2, Zap,
} from "lucide-react";
import StripeStatusPanel from "./StripeStatusPanel";
import StripeEventsPanel from "./StripeEventsPanel";

const RESEND_FIELDS = [
  { name: "RESEND_API_KEY", label: "RESEND_API_KEY", secret: true,
    placeholder: "re_...", help: "Live API-sleutel uit het Resend-dashboard." },
  { name: "FROM_EMAIL", label: "FROM_EMAIL", secret: false,
    placeholder: "5999Cargo <noreply@5999cargo.nl>", help: "Afzender. Naam-formaat ondersteund." },
  { name: "ADMIN_NOTIFICATION_EMAIL", label: "ADMIN_NOTIFICATION_EMAIL", secret: false,
    placeholder: "info@5999cargo.nl", help: "Adres dat interne notificaties (offertes, contact) ontvangt." },
  { name: "ADMIN_BACKOFFICE_URL", label: "ADMIN_BACKOFFICE_URL", secret: false,
    placeholder: "https://backoffice.5999cargo.nl/admin", help: "Volledige URL die in admin-mails als deeplink wordt gebruikt." },
  { name: "DOCUMENT_COPY_EMAIL", label: "DOCUMENT_COPY_EMAIL", secret: false,
    placeholder: "invoice@5999cargo.nl", help: "Adres dat een kopie ontvangt van elke factuur/offerte/paklijst/boekingsformulier (Phase 18.3)." },
];

const STRIPE_FIELDS = [
  { name: "STRIPE_SECRET_KEY", label: "STRIPE_SECRET_KEY", secret: true,
    placeholder: "sk_live_... of sk_test_...", help: "Server-side Stripe key. LIVE = echte transacties." },
  { name: "REACT_APP_STRIPE_PUBLISHABLE_KEY", label: "REACT_APP_STRIPE_PUBLISHABLE_KEY", secret: true,
    placeholder: "pk_live_... of pk_test_...", help: "Publishable key (frontend). Mode moet matchen met Secret." },
  { name: "STRIPE_WEBHOOK_SECRET", label: "STRIPE_WEBHOOK_SECRET", secret: true,
    placeholder: "whsec_...", help: "Endpoint signing secret. Zonder deze: orders worden niet automatisch op paid gezet." },
  { name: "STRIPE_SUCCESS_URL", label: "STRIPE_SUCCESS_URL", secret: false,
    placeholder: "https://www.5999cargo.nl/webshop/success?session_id={CHECKOUT_SESSION_ID}", help: "Optioneel. Standaard: origin/webshop/success." },
  { name: "STRIPE_CANCEL_URL", label: "STRIPE_CANCEL_URL", secret: false,
    placeholder: "https://www.5999cargo.nl/webshop/cancel", help: "Optioneel. Standaard: origin/webshop/cancel." },
];

const SOURCE_LABELS = {
  db_override: { label: "Backoffice", className: "bg-blue-100 text-blue-700" },
  env: { label: "Server .env", className: "bg-gray-200 text-gray-700" },
  missing: { label: "Niet ingesteld", className: "bg-red-100 text-red-700" },
};

function detectStripeMode(preview) {
  if (!preview) return "missing";
  const v = preview.toLowerCase();
  if (v.startsWith("sk_live_") || v.startsWith("pk_live_")) return "live";
  if (v.startsWith("sk_test_") || v.startsWith("pk_test_")) return "test";
  return "unknown";
}

function MaskedField({ field, status, value, onChange, onClear, busy }) {
  const [reveal, setReveal] = useState(false);
  const [edited, setEdited] = useState(false);
  const isSecret = field.secret;
  const fStatus = status?.fields?.[field.name];
  const sourceMeta = SOURCE_LABELS[fStatus?.source] || SOURCE_LABELS.missing;
  const placeholder = useMemo(() => {
    if (!fStatus?.configured) return field.placeholder;
    if (isSecret) return `${fStatus.preview} (${fStatus.length} tekens)`;
    return fStatus.value ?? field.placeholder;
  }, [fStatus, field, isSecret]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start py-3 border-b border-gray-100 last:border-b-0" data-testid={`prod-config-row-${field.name}`}>
      <div className="md:col-span-4">
        <Label htmlFor={`f-${field.name}`} className="text-[11px] tracking-wider uppercase font-chivo font-bold text-[#0A192F]">{field.label}</Label>
        <div className="mt-1 flex items-center gap-2">
          <span className={`text-[10px] px-2 py-0.5 tracking-wider uppercase font-chivo font-bold ${sourceMeta.className}`} data-testid={`prod-config-source-${field.name}`}>
            {sourceMeta.label}
          </span>
          {fStatus?.configured ? (
            <span className="text-[10px] inline-flex items-center gap-1 text-green-700"><CheckCircle2 className="w-3 h-3" /> Geconfigureerd</span>
          ) : (
            <span className="text-[10px] inline-flex items-center gap-1 text-red-700"><XCircle className="w-3 h-3" /> Ontbreekt</span>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-1">{field.help}</p>
        {isSecret && fStatus?.configured && (
          <div className="text-xs text-gray-700 mt-1 font-mono" data-testid={`prod-config-mask-${field.name}`}>
            Huidig: <span className="bg-gray-100 px-1 py-0.5">{fStatus.preview}</span>
          </div>
        )}
        {fStatus?.updated_by && (
          <div className="text-[10px] text-gray-400 mt-1">
            Laatst gewijzigd door {fStatus.updated_by} {fStatus.updated_at && `op ${new Date(fStatus.updated_at).toLocaleString("nl-NL")}`}
          </div>
        )}
      </div>
      <div className="md:col-span-8 space-y-2">
        <div className="flex items-center gap-2">
          <Input
            id={`f-${field.name}`}
            type={isSecret && !reveal ? "password" : "text"}
            value={value}
            placeholder={placeholder}
            data-testid={`prod-config-input-${field.name}`}
            onChange={(e) => { setEdited(true); onChange(e.target.value); }}
            autoComplete="off"
            className="flex-1 rounded-none"
            disabled={busy}
          />
          {isSecret && (
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="rounded-none"
              data-testid={`prod-config-reveal-${field.name}`}
              onClick={() => setReveal(r => !r)}
              title={reveal ? "Verberg" : "Toon ingevoerde waarde"}
            >
              {reveal ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          )}
          {fStatus?.source === "db_override" && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-none border-red-200 text-red-700 hover:bg-red-50"
              data-testid={`prod-config-clear-${field.name}`}
              onClick={() => onClear(field)}
              disabled={busy}
            >
              <Trash2 className="w-3 h-3 mr-1" /> Wis override
            </Button>
          )}
        </div>
        {edited && isSecret && (
          <div className="text-[11px] text-amber-700">
            Nieuwe waarde wordt versleuteld opgeslagen. We tonen deze nooit meer in plaintext na opslaan.
          </div>
        )}
      </div>
    </div>
  );
}

export default function ProductionConfigSection() {
  const { hasPermission } = useAuth();
  const canView = hasPermission("settings:production_view");
  const canEdit = hasPermission("settings:production_update");

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({}); // { fieldName: string }
  const [confirmLive, setConfirmLive] = useState(null); // { section, payload }
  const [clearing, setClearing] = useState(null); // field
  const [tab, setTab] = useState("resend");
  const [testEmailResult, setTestEmailResult] = useState(null);
  const [testStripeResult, setTestStripeResult] = useState(null);
  const [testing, setTesting] = useState({ email: false, stripe: false });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/production/config");
      setStatus(r.data);
      setDraft({}); // reset edited values after reload
    } catch (e) {
      toast.error(formatApiError(e, "Configuratie laden mislukt"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (canView) refresh(); }, [canView, refresh]);

  if (!canView) {
    return (
      <div className="bg-white border border-gray-200 p-10 text-center" data-testid="prod-config-no-access">
        <h2 className="font-chivo font-black text-xl text-[#0A192F]">Geen toegang</h2>
        <p className="text-sm text-gray-600 mt-2">Alleen Owner en Admin kunnen productiekeys beheren.</p>
      </div>
    );
  }

  const stripeKeyEntry = status?.fields?.STRIPE_SECRET_KEY;
  const pubKeyEntry = status?.fields?.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  const stripeMode = detectStripeMode(stripeKeyEntry?.preview);
  const pubMode = detectStripeMode(pubKeyEntry?.preview);
  const modeMismatch = stripeKeyEntry?.configured && pubKeyEntry?.configured && stripeMode !== pubMode;

  const setVal = (name, v) => setDraft((d) => ({ ...d, [name]: v }));

  const buildUpdates = (fields) =>
    fields
      .filter((f) => Object.prototype.hasOwnProperty.call(draft, f.name))
      .map((f) => ({ name: f.name, value: draft[f.name] }));

  const containsLiveKey = (updates) =>
    updates.some(
      (u) =>
        (u.name === "STRIPE_SECRET_KEY" && u.value && u.value.startsWith("sk_live_")) ||
        (u.name === "REACT_APP_STRIPE_PUBLISHABLE_KEY" && u.value && u.value.startsWith("pk_live_"))
    );

  const doSave = async (sectionLabel, updates) => {
    if (!updates.length) {
      toast.info("Geen wijzigingen.");
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.put("/admin/production/config", { updates });
      setStatus(r.data.status);
      setDraft({});
      toast.success(`${sectionLabel} opgeslagen (${r.data.applied?.length || 0} velden, ${r.data.cleared?.length || 0} gewist)`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.validation) {
        const msgs = Object.entries(detail.validation).map(([k, v]) => `${k}: ${v}`).join("\n");
        toast.error(`Validatiefout:\n${msgs}`);
      } else {
        toast.error(formatApiError(e, "Opslaan mislukt"));
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSave = (sectionLabel, fields) => {
    const updates = buildUpdates(fields);
    if (containsLiveKey(updates)) {
      setConfirmLive({ sectionLabel, updates });
      return;
    }
    doSave(sectionLabel, updates);
  };

  const handleClear = (field) => setClearing(field);

  const confirmClear = async () => {
    if (!clearing) return;
    setBusy(true);
    try {
      const r = await adminApi.put("/admin/production/config", {
        updates: [{ name: clearing.name, value: "" }],
      });
      setStatus(r.data.status);
      setDraft((d) => { const c = { ...d }; delete c[clearing.name]; return c; });
      toast.success(`${clearing.name} gewist — terug naar server .env / default.`);
    } catch (e) {
      toast.error(formatApiError(e, "Wissen mislukt"));
    } finally {
      setBusy(false);
      setClearing(null);
    }
  };

  const runTestEmail = async () => {
    setTesting((s) => ({ ...s, email: true }));
    setTestEmailResult(null);
    try {
      const r = await adminApi.post("/admin/production/test/resend");
      setTestEmailResult(r.data);
      if (r.data.status === "sent") toast.success("Test e-mail verstuurd.");
      else toast.warning(r.data.message || "Test e-mail werd niet verzonden.");
    } catch (e) {
      toast.error(formatApiError(e, "Test e-mail mislukt"));
    } finally {
      setTesting((s) => ({ ...s, email: false }));
    }
  };

  const runTestStripe = async () => {
    setTesting((s) => ({ ...s, stripe: true }));
    setTestStripeResult(null);
    try {
      const r = await adminApi.post("/admin/production/test/stripe");
      setTestStripeResult(r.data);
      if (r.data.status === "ok") toast.success(`Stripe key werkt — mode: ${r.data.mode}`);
      else toast.warning(r.data.message || "Stripe-test gaf een waarschuwing.");
    } catch (e) {
      toast.error(formatApiError(e, "Stripe-test mislukt"));
    } finally {
      setTesting((s) => ({ ...s, stripe: false }));
    }
  };

  const encOk = !!status?.encryption_available;

  return (
    <div className="space-y-6" data-testid="prod-config-section">
      <div className="flex flex-col gap-2">
        <h1 className="font-chivo font-black text-2xl text-[#0A192F] flex items-center gap-2">
          <Lock className="w-6 h-6 text-[#0055FF]" /> Productieconfiguratie
        </h1>
        <p className="text-sm text-gray-600 max-w-3xl">
          Beheer Resend en Stripe live keys zonder server-deploy. Alle waarden worden versleuteld opgeslagen en
          alleen gemaskeerd weergegeven. Wijzigingen werken direct, zonder backend-restart.
        </p>
      </div>

      {/* Encryption banner */}
      <div
        className={`border-l-4 p-4 ${encOk ? "border-green-500 bg-green-50" : "border-red-500 bg-red-50"}`}
        data-testid="prod-config-encryption-banner"
      >
        <div className="flex items-start gap-3">
          {encOk ? (
            <ShieldCheck className="w-5 h-5 text-green-700 mt-0.5" />
          ) : (
            <ShieldAlert className="w-5 h-5 text-red-700 mt-0.5" />
          )}
          <div className="text-sm">
            {encOk ? (
              <>
                <span className="font-chivo font-bold text-green-800">APP_ENCRYPTION_KEY actief.</span>{" "}
                <span className="text-gray-700">Secrets worden versleuteld at rest opgeslagen via Fernet (AES-128 CBC + HMAC-SHA256).</span>
              </>
            ) : (
              <>
                <span className="font-chivo font-bold text-red-800">APP_ENCRYPTION_KEY ontbreekt.</span>{" "}
                <span className="text-gray-700">Voeg deze toe aan backend/.env voordat je secrets opslaat. Niet-secret waarden kunnen wel worden bewaard.</span>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Live key warning */}
      <div className="border-l-4 border-amber-500 bg-amber-50 p-4 text-sm text-amber-900" data-testid="prod-config-live-warning">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
          <div>
            <div className="font-chivo font-bold mb-1">Let op bij overschakelen test → live</div>
            Test-mode Stripe IDs werken niet met live keys. Na het invullen van live keys moeten alle actieve
            producten en varianten opnieuw naar Stripe worden gesynchroniseerd via{" "}
            <span className="font-chivo font-bold">Webshopbeheer → Stripe → Markeer alles als sync nodig</span>.
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} data-testid="prod-config-tabs">
        <TabsList className="rounded-none">
          <TabsTrigger value="resend" data-testid="prod-config-tab-resend"><Mail className="w-4 h-4 mr-2" /> Resend (e-mail)</TabsTrigger>
          <TabsTrigger value="stripe" data-testid="prod-config-tab-stripe"><CreditCard className="w-4 h-4 mr-2" /> Stripe (payments)</TabsTrigger>
          <TabsTrigger value="stripe_status" data-testid="prod-config-tab-stripe-status"><Zap className="w-4 h-4 mr-2" /> Stripe status</TabsTrigger>
          <TabsTrigger value="stripe_events" data-testid="prod-config-tab-stripe-events"><AlertTriangle className="w-4 h-4 mr-2" /> Stripe events</TabsTrigger>
        </TabsList>

        {/* RESEND TAB */}
        <TabsContent value="resend" className="space-y-4 mt-4">
          {loading ? (
            <div className="bg-white border border-gray-200 p-6 text-sm text-gray-500">Laden…</div>
          ) : (
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h2 className="font-chivo font-bold text-lg text-[#0A192F]">Resend transactional e-mail</h2>
                  <p className="text-xs text-gray-500">Velden hieronder overschrijven .env-waarden zodra ze zijn opgeslagen.</p>
                </div>
              </div>
              <div className="divide-y divide-gray-100">
                {RESEND_FIELDS.map((f) => (
                  <MaskedField
                    key={f.name}
                    field={f}
                    status={status}
                    value={draft[f.name] ?? ""}
                    onChange={(v) => setVal(f.name, v)}
                    onClear={handleClear}
                    busy={busy}
                  />
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <Button
                  type="button"
                  data-testid="prod-config-save-resend"
                  className="rounded-none bg-[#0055FF] hover:bg-[#0045D0] text-white"
                  onClick={() => handleSave("Resend configuratie", RESEND_FIELDS)}
                  disabled={!canEdit || busy || Object.keys(draft).filter((k) => RESEND_FIELDS.find(f => f.name === k)).length === 0}
                >
                  <Save className="w-4 h-4 mr-2" /> Opslaan Resend configuratie
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="prod-config-test-email"
                  className="rounded-none"
                  onClick={runTestEmail}
                  disabled={!canEdit || testing.email}
                >
                  {testing.email ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Test e-mail verzenden
                </Button>
              </div>
              {testEmailResult && (
                <div
                  className={`mt-3 p-3 text-xs ${
                    testEmailResult.status === "sent"
                      ? "bg-green-50 border border-green-200 text-green-800"
                      : "bg-amber-50 border border-amber-200 text-amber-800"
                  }`}
                  data-testid="prod-config-test-email-result"
                >
                  <div className="font-chivo font-bold uppercase tracking-wide">
                    Resultaat: {testEmailResult.status}
                  </div>
                  <div>{testEmailResult.message}</div>
                  {testEmailResult.detail && <div className="mt-1 text-[11px]">{String(testEmailResult.detail).slice(0, 200)}</div>}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* STRIPE TAB */}
        <TabsContent value="stripe" className="space-y-4 mt-4">
          {loading ? (
            <div className="bg-white border border-gray-200 p-6 text-sm text-gray-500">Laden…</div>
          ) : (
            <div className="bg-white border border-gray-200 p-6">
              <div className="flex flex-wrap items-center gap-3 mb-3">
                <h2 className="font-chivo font-bold text-lg text-[#0A192F]">Stripe payments</h2>
                {stripeKeyEntry?.configured && (
                  <span
                    className={`text-[11px] tracking-wider uppercase font-chivo font-bold px-2 py-0.5 ${
                      stripeMode === "live"
                        ? "bg-red-100 text-red-700"
                        : stripeMode === "test"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                    data-testid="prod-config-stripe-mode-badge"
                  >
                    Mode: {stripeMode === "live" ? "LIVE" : stripeMode === "test" ? "TEST" : "ONBEKEND"}
                  </span>
                )}
                {modeMismatch && (
                  <span className="text-[11px] tracking-wider uppercase font-chivo font-bold px-2 py-0.5 bg-orange-100 text-orange-700" data-testid="prod-config-stripe-mode-mismatch">
                    Mode-mismatch: secret={stripeMode}, publishable={pubMode}
                  </span>
                )}
              </div>
              <p className="text-xs text-gray-500 mb-2">Velden hieronder overschrijven .env-waarden. Mode wordt automatisch gedetecteerd uit de key prefix.</p>
              <div className="divide-y divide-gray-100">
                {STRIPE_FIELDS.map((f) => (
                  <MaskedField
                    key={f.name}
                    field={f}
                    status={status}
                    value={draft[f.name] ?? ""}
                    onChange={(v) => setVal(f.name, v)}
                    onClear={handleClear}
                    busy={busy}
                  />
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 mt-4">
                <Button
                  type="button"
                  data-testid="prod-config-save-stripe"
                  className="rounded-none bg-[#0055FF] hover:bg-[#0045D0] text-white"
                  onClick={() => handleSave("Stripe configuratie", STRIPE_FIELDS)}
                  disabled={!canEdit || busy || Object.keys(draft).filter((k) => STRIPE_FIELDS.find(f => f.name === k)).length === 0}
                >
                  <Save className="w-4 h-4 mr-2" /> Opslaan Stripe configuratie
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  data-testid="prod-config-test-stripe"
                  className="rounded-none"
                  onClick={runTestStripe}
                  disabled={!canEdit || testing.stripe}
                >
                  {testing.stripe ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Test Stripe configuratie
                </Button>
              </div>
              {testStripeResult && (
                <div
                  className={`mt-3 p-3 text-xs ${
                    testStripeResult.status === "ok"
                      ? "bg-green-50 border border-green-200 text-green-800"
                      : "bg-amber-50 border border-amber-200 text-amber-800"
                  }`}
                  data-testid="prod-config-test-stripe-result"
                >
                  <div className="font-chivo font-bold uppercase tracking-wide">
                    Resultaat: {testStripeResult.status}
                  </div>
                  <div>{testStripeResult.message}</div>
                  {testStripeResult.status === "ok" && (
                    <ul className="mt-1 list-disc pl-5 space-y-0.5">
                      <li>Mode: {testStripeResult.mode}</li>
                      {testStripeResult.account_country && <li>Land: {testStripeResult.account_country}</li>}
                      <li>Charges: {String(testStripeResult.charges_enabled)}</li>
                      <li>Payouts: {String(testStripeResult.payouts_enabled)}</li>
                    </ul>
                  )}
                  {testStripeResult.detail && <div className="mt-1 text-[11px]">{String(testStripeResult.detail).slice(0, 300)}</div>}
                </div>
              )}
            </div>
          )}
        </TabsContent>

        {/* STRIPE STATUS TAB */}
        <TabsContent value="stripe_status" className="space-y-4 mt-4">
          <StripeStatusPanel />
        </TabsContent>

        {/* STRIPE EVENTS TAB */}
        <TabsContent value="stripe_events" className="space-y-4 mt-4">
          <StripeEventsPanel />
        </TabsContent>
      </Tabs>

      {/* Live key confirm dialog */}
      <Dialog open={!!confirmLive} onOpenChange={(o) => !o && setConfirmLive(null)}>
        <DialogContent data-testid="prod-config-confirm-live-dialog">
          <DialogHeader>
            <DialogTitle>Live keys opslaan?</DialogTitle>
            <DialogDescription>
              Je staat op het punt productie-keys op te slaan. Live keys verwerken echte transacties — controleer
              dat dit het juiste account is en dat product- en variantsync opnieuw wordt uitgevoerd na opslaan.
            </DialogDescription>
          </DialogHeader>
          <ul className="text-xs text-gray-700 space-y-1 list-disc pl-5">
            {(confirmLive?.updates || []).map((u) => (
              <li key={u.name}><span className="font-chivo font-bold">{u.name}</span>: {u.value ? `nieuwe waarde (${u.value.length} tekens)` : "wissen"}</li>
            ))}
          </ul>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setConfirmLive(null)} data-testid="prod-config-confirm-cancel">
              Annuleren
            </Button>
            <Button
              className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              onClick={async () => {
                const cl = confirmLive;
                setConfirmLive(null);
                if (cl) await doSave(cl.sectionLabel, cl.updates);
              }}
              data-testid="prod-config-confirm-save"
            >
              Ja, opslaan als live
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clear confirm dialog */}
      <Dialog open={!!clearing} onOpenChange={(o) => !o && setClearing(null)}>
        <DialogContent data-testid="prod-config-confirm-clear-dialog">
          <DialogHeader>
            <DialogTitle>Override wissen?</DialogTitle>
            <DialogDescription>
              {clearing && (
                <>
                  Je verwijdert de Backoffice-override voor <span className="font-chivo font-bold">{clearing.name}</span>.
                  Het systeem valt terug op de waarde uit server-omgeving (.env) of de standaard. Dit kan niet ongedaan
                  worden gemaakt — voor secrets moet je de waarde opnieuw invoeren.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => setClearing(null)} data-testid="prod-config-clear-cancel">
              Annuleren
            </Button>
            <Button
              className="rounded-none bg-red-600 hover:bg-red-700 text-white"
              onClick={confirmClear}
              data-testid="prod-config-clear-confirm"
            >
              <Trash2 className="w-4 h-4 mr-2" /> Wissen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
